/**
 * Mapeo `zone -> color Tailwind` para la barra apilada de zonas de
 * intensidad. Se mantiene aparte del componente porque también lo usa la
 * vista detalle de entreno (`WorkoutDetail.tsx`), así evitamos duplicar la
 * tabla de colores y mantener la consistencia visual.
 *
 * Reglas (extraídas del contrato M2.6):
 *   recovery / z1            → emerald-200
 *   z2                       → emerald-400
 *   z3                       → amber-300
 *   z4                       → orange-400
 *   z5 / z6 / z7 / threshold
 *   / vo2max                 → rose-500
 *   unzoned / desconocido    → slate-300
 *
 * Devolvemos un objeto con la clase Tailwind y un hex equivalente para que
 * el SVG (donut, mini-barras) pueda pintar sin depender del runtime de
 * Tailwind (los segmentos de un SVG no soportan `class="bg-*"`).
 */
export type ZoneColor = {
  /** Clase Tailwind para `<div>` (barra horizontal). */
  className: string;
  /** Hex equivalente para `<rect>`/`<path>` SVG. */
  hex: string;
  /** Etiqueta normalizada para la leyenda (en español o tal cual). */
  label: string;
};

const PALETTE = {
  emerald200: { className: 'bg-emerald-200', hex: '#a7f3d0' },
  emerald400: { className: 'bg-emerald-400', hex: '#34d399' },
  amber300: { className: 'bg-amber-300', hex: '#fcd34d' },
  orange400: { className: 'bg-orange-400', hex: '#fb923c' },
  rose500: { className: 'bg-rose-500', hex: '#f43f5e' },
  slate300: { className: 'bg-slate-300', hex: '#cbd5e1' },
} as const;

/**
 * Normaliza una etiqueta de zona a su color. Acepta variantes de mayúsculas
 * y prefijos como "Z2", "z2", "Zona 2" si llegan del backend.
 */
export function zoneToColor(zone: string): ZoneColor {
  const z = zone.trim().toLowerCase();
  if (z === 'recovery' || z === 'z1' || z === 'zona 1' || z === 'rec') {
    return { ...PALETTE.emerald200, label: zone };
  }
  if (z === 'z2' || z === 'zona 2' || z === 'endurance') {
    return { ...PALETTE.emerald400, label: zone };
  }
  if (z === 'z3' || z === 'zona 3' || z === 'tempo') {
    return { ...PALETTE.amber300, label: zone };
  }
  if (z === 'z4' || z === 'zona 4') {
    return { ...PALETTE.orange400, label: zone };
  }
  if (
    z === 'z5' ||
    z === 'z6' ||
    z === 'z7' ||
    z === 'threshold' ||
    z === 'vo2max' ||
    z === 'vo2'
  ) {
    return { ...PALETTE.rose500, label: zone };
  }
  // 'unzoned', null serializado, valores raros → gris neutro.
  return { ...PALETTE.slate300, label: zone || 'sin zona' };
}
